import { SetOptional } from 'type-fest'

import api from '@libs/api'

import DateUtils from '@utils/dates'
import { handleServiceError } from '@utils/errors'
import {
  IApiData,
  IEntityValidation,
  IPressPost,
  IQueryFilters,
  PressPostStatus,
} from '@utils/types'
import { buildQueryString } from '@utils/url'

const findAllAsAdmin = async (
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
): Promise<IApiData<IPressPost[]>> => {
  const queryString = buildQueryString(queryFilters)

  try {
    const response = await api.get({
      path: '/press-posts',
      queryString,
    })

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findAllAsVisitor = async (
  queryFilters?: SetOptional<IQueryFilters, 'page' | 'limit'>
): Promise<IApiData<IPressPost[]>> => {
  const queryString = buildQueryString(queryFilters)

  try {
    const response = await api.get({
      path: '/press-posts/public',
      queryString,
      authenticate: false,
    })

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const findById = async (id: string) => {
  try {
    const response = await api.get({
      path: `/press-posts/${id}`,
    })

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const create = async ({
  pressPost,
}: {
  pressPost: Pick<IPressPost, 'title'> & Partial<IPressPost>
}) => {
  const { title, image, externalUrl, publishedDate } = pressPost
  const form = api.buildFormData({ title, image, externalUrl, publishedDate })

  try {
    const response = (await api.post({
      path: '/press-posts',
      data: form,
      headers: { 'Content-Type': 'multipart/form-data' },
    })) as IPressPost

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const update = async ({
  pressPost,
  deleteImage,
}: {
  pressPost: Pick<IPressPost, 'id' | 'title'> & Partial<IPressPost>
  deleteImage?: boolean
}) => {
  const { id, title, image, externalUrl, publishedDate } = pressPost
  const form = api.buildFormData({
    title,
    image,
    externalUrl,
    publishedDate,
    deleteImage,
  })

  try {
    const response = (await api.put({
      path: `/press-posts/${id}`,
      data: form,
      headers: { 'Content-Type': 'multipart/form-data' },
    })) as IPressPost

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const updateStatus = async ({
  id,
  status: newStatus,
}: Pick<IPressPost, 'id' | 'status'>): Promise<IPressPost> => {
  const form = api.buildFormData({ status: newStatus })
  try {
    const response = (await api.put({
      path: `/press-posts/${id}/update-status`,
      data: form,
      headers: { 'Content-Type': 'multipart/form-data' },
    })) as IPressPost

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}
const validatePost = async ({
  id,
}: Pick<IPressPost, 'id'>): Promise<IEntityValidation> => {
  try {
    const response = (await api.get({
      path: `/press-posts/${id}/validate`,
    })) as IPressPost

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

const publish = ({ id }: Pick<IPressPost, 'id'>) =>
  updateStatus({ id, status: PressPostStatus.PUBLISHED })
const unpublish = ({ id }: Pick<IPressPost, 'id'>) =>
  updateStatus({ id, status: PressPostStatus.DRAFT })

const remove = async ({ id }: Pick<IPressPost, 'id'>) => {
  try {
    const response = (await api.remove({
      path: `/press-posts/${id}`,
    })) as IPressPost

    return DateUtils.replaceISODateWithDate(response)
  } catch (err: any) {
    throw handleServiceError(err)
  }
}

export default {
  create,
  update,
  findAllAsAdmin,
  findAllAsVisitor,
  findById,
  validatePost,
  publish,
  unpublish,
  remove,
}
